import { VideoQuality } from "livekit-client";

/**
 * Publish-side bitrate presets. This is the single most important knob: every
 * student receives the presenter's stream, so the per-student figure decides
 * both their data bill and whether the class scales.
 *
 * gbPerHour = (mbps * 3600) / 8 / 1000
 */
export const QUALITY_PRESETS = {
  light: {
    key: "light",
    label: "Data Saver",
    short: "0.8 Mbps",
    share: 600_000,
    cam: 200_000,
    perStudent: "~0.8 Mbps",
    perHour: "≈ 0.35 GB / hour",
    hint: "Softest text · weakest networks · 600+ students",
  },
  balanced: {
    key: "balanced",
    label: "Standard HD",
    short: "1.5 Mbps",
    share: 1_200_000,
    cam: 350_000,
    perStudent: "~1.5 Mbps",
    perHour: "≈ 0.7 GB / hour",
    hint: "Middle ground · easy on student data",
  },
  sharp: {
    key: "sharp",
    label: "Max Quality",
    short: "3.5 Mbps",
    share: 3_500_000,
    cam: 800_000,
    perStudent: "~1.1 Mbps at 150 students",
    perHour: "≈ 0.5 GB / hour per student",
    hint: "Default · sharpest text · 150 students fit the 200 Mbps line",
  },
};

const capEncodings = async (sender, ceiling) => {
  if (!sender || typeof sender.getParameters !== "function") return false;
  const params = sender.getParameters();
  if (!params.encodings || params.encodings.length === 0) return false;
  // Highest layer gets the ceiling. The LOWER layers are what a big class
  // actually receives, so they get a real share of the ceiling instead of a
  // third-of-a-third — a 150-student class watching the middle layer used to
  // be handed ~0.4 Mbps, which is exactly why the slide looked blurry.
  //   top  = ceiling            (small classes / strong links)
  //   mid  = 31% of the ceiling (720p — the tier a 150-student class sits on)
  //   low  = 20% of the ceiling (540p — weak links)
  const SHARE = [1, 0.31, 0.2];
  const ordered = [...params.encodings].sort(
    (a, b) => (a.scaleResolutionDownBy || 1) - (b.scaleResolutionDownBy || 1)
  );
  ordered.forEach((enc, i) => {
    enc.maxBitrate = Math.max(120_000, Math.round(ceiling * (SHARE[i] ?? 0.15)));
  });
  // A slide that stutters is unwatchable; a slide that softens for a second is
  // fine. Tell the encoder to protect frame rate over resolution.
  params.degradationPreference = "maintain-framerate";
  await sender.setParameters(params);
  return true;
};

/** Applies a preset to whatever the local participant is publishing right now. */
export async function applyPublishQuality(room, presetKey) {
  const preset = QUALITY_PRESETS[presetKey] || QUALITY_PRESETS.sharp;
  const lp = room?.localParticipant;
  if (!lp) return { applied: 0, preset };
  let applied = 0;
  const pubs = Array.from(lp.videoTrackPublications?.values?.() || []);
  for (const pub of pubs) {
    const sender = pub?.track?.sender;
    const ceiling = pub.source === "screen_share" ? preset.share : preset.cam;
    try {
      if (await capEncodings(sender, ceiling)) applied += 1;
    } catch (_) {}
  }
  return { applied, preset };
}

/**
 * Student-side data saver: stop pulling every camera, keep the presenter's
 * slide and audio flowing. Audio is never touched — the teacher's voice is the
 * one thing that must not break.
 */
export function setLowDataMode(room, on) {
  const results = { cameras: 0, share: false };
  if (!room) return results;
  room.remoteParticipants?.forEach((p) => {
    p.videoTrackPublications?.forEach((pub) => {
      try {
        if (pub.source === "screen_share") {
          pub.setVideoQuality?.(on ? VideoQuality.LOW : VideoQuality.HIGH);
          results.share = true;
        } else {
          pub.setEnabled?.(!on);
          results.cameras += 1;
        }
      } catch (_) {}
    });
  });
  return results;
}

/**
 * What the STUDENT chooses to receive. This is a ceiling, not a floor —
 * LiveKit's SFU still steps them down to a lower simulcast layer the moment
 * their link struggles and climbs back when it recovers, so audio never
 * stutters even on the 1080p setting.
 */
export const VIEW_QUALITY = {
  "1080p": { key: "1080p", label: "1080p", quality: VideoQuality.HIGH, w: 1920, h: 1080, mbps: 2.5, hint: "Full HD · auto-adjusts if your net dips" },
  "720p": { key: "720p", label: "720p", quality: VideoQuality.MEDIUM, w: 1280, h: 720, mbps: 1.2, hint: "Sharp and light on data" },
  "540p": { key: "540p", label: "540p", quality: VideoQuality.LOW, w: 960, h: 540, mbps: 0.6, hint: "Easy on 3G / crowded wifi" },
  "360p": { key: "360p", label: "360p", quality: VideoQuality.LOW, w: 640, h: 360, mbps: 0.6, hint: "Lowest data — voice stays perfect" },
};

const LADDER = ["1080p", "720p", "540p", "360p"];

/**
 * A class is NEVER turned away for being full. The video server shares one
 * 200 Mbit pipe between everyone in the room, so instead of locking student
 * #141 out, the slide simply steps down a rung and everybody stays in.
 *
 * Budget maths on the 200 Mbit port (Max Quality preset, 3.5 Mbps ceiling):
 *   1080p top layer   3.5 Mbps  x  50 students = 175 Mbit  ✓
 *   720p  mid layer   1.09 Mbps x 160 students = 174 Mbit  ✓  (150 → 164 Mbit)
 *   540p  low layer   0.70 Mbps x 280 students = 196 Mbit  ✓
 * Audio adds ~40 kbps per student (150 → 6 Mbit), cameras are off in class.
 *
 *   up to  50 students → 1080p
 *         160          → 720p   ← a 150-student class lives here
 *         280          → 540p
 *       beyond that    → 540p + cameras dropped, slide and voice only
 *
 * The teacher gets told when it steps down; students just see a room that
 * keeps working.
 */
export const TIERS = [
  { upTo: 50, key: "1080p" },
  { upTo: 160, key: "720p" },
  { upTo: 280, key: "540p" },
  { upTo: Infinity, key: "540p" },
];

/** Past this, incoming cameras are dropped so the slide keeps its bandwidth. */
export const SLIDE_ONLY_FROM = 200;

export function qualityCeiling(headcount = 0) {
  let ceiling = TIERS.find((t) => headcount <= t.upTo)?.key || "540p";
  if (typeof window !== "undefined") {
    const shortest = Math.min(window.innerWidth || 0, window.innerHeight || 0);
    // A phone cannot display more than ~720p — asking for Full HD is pure
    // bandwidth thrown away, for the teacher and for the student's data pack.
    // Measure the SHORT edge: a portrait phone is ~390x844, so the long edge
    // is no help in telling a phone from a laptop.
    if (shortest > 0 && shortest <= 500 && ceiling === "1080p") ceiling = "720p";
  }
  return ceiling;
}

export function capQuality(key, headcount = 0) {
  const want = LADDER.indexOf(VIEW_QUALITY[key] ? key : "1080p");
  const cap = LADDER.indexOf(qualityCeiling(headcount));
  const chosen = LADDER[Math.max(want, cap)];
  return { preset: VIEW_QUALITY[chosen], capped: chosen !== LADDER[want] };
}

/**
 * Drops (or restores) incoming CAMERA video only — the shared slide and all
 * audio are untouched. Used when a class gets crowded enough that the cameras
 * would eat the bandwidth the slide needs.
 */
export function dropRemoteCameras(room, drop) {
  let touched = 0;
  room?.remoteParticipants?.forEach((p) => {
    p.videoTrackPublications?.forEach((pub) => {
      if (pub.source === "screen_share") return;
      try {
        pub.setEnabled?.(!drop);
        touched += 1;
      } catch (_) {}
    });
  });
  return touched;
}

/** Applies the student's chosen ceiling to every incoming video track. */export function applyViewQuality(room, key, headcount = 0) {
  const { preset: v, capped } = capQuality(key, headcount);
  let touched = 0;
  room?.remoteParticipants?.forEach((p) => {
    p.videoTrackPublications?.forEach((pub) => {
      try {
        pub.setVideoQuality?.(v.quality);
        pub.setVideoDimensions?.({ width: v.w, height: v.h });
        touched += 1;
      } catch (_) {}
    });
  });
  return { touched, preset: v, capped };
}
